import cx from 'clsx';
import { forwardRef, ReactNode, type ReactElement } from 'react';
import type { ActivityType } from 'types';
import type { Transparency } from 'modules/core/types';
import { Box, BoxProps, Icon, type IconProp, type IconProps } from '@ff-it/ui';

export type IconSize = IconProps['size'];
export { Icon, type IconProp, type IconProps };

// to use as popover anchor
export const WrappedIcon = forwardRef<HTMLSpanElement, IconProps & { children?: ReactNode }>(
  ({ children, ...props }, ref): ReactElement => {
    return (
      <Box as="span" position="relative" ref={ref}>
        {children}
        <Icon {...props} />
      </Box>
    );
  },
);

// @TODO fixme sync wityh icons in TYPE_ICON_MAP in backend

export const typeIconMap: Record<ActivityType, IconProp> = {
  TV: 'tv',
  RADIO: 'radio',
  OOH: 'billboard',
  PRINT: 'newspaper',
  WEB: 'globe',
  SOCIAL: 'chart-network',
  CREATIVE: 'palette',
  RTB: 'gavel',
  OTHER: 'ellipsis-vertical',
  BTL: 'down-from-line',
  SEO: 'magnifying-glass',
};

export function ActivityIcon({
  type,
  className,
  size,
}: {
  type: ActivityType;
  className?: string;
  size?: IconSize;
}): ReactElement {
  return <Icon icon={typeIconMap[type]} className={className} size={size} title={type} />;
}

const transparencyIconMap: Record<Transparency, IconProp> = {
  PLANNED: 'paper-plane',
  DELIVERED: 'square-poll-vertical',
};

export function TransparencyIcon({
  transparency,
  className,
  size,
}: {
  transparency: Transparency;
  className?: string;
  size?: IconSize;
}): ReactElement {
  return <Icon icon={transparencyIconMap[transparency]} className={cx('transp-icon', className)} size={size} />;
}

export function BoolIcon({ children }: { children: boolean }): ReactElement {
  // @TODO color
  return <Icon className={children ? 'text-success' : 'text-danger'} icon={children ? 'check' : 'circle-xmark'} />;
}

export function FeaturedIcon({ icon, ...rest }: { icon: IconProp } & BoxProps): ReactElement {
  return (
    <Box
      color="primary"
      background="brand25"
      p="1.5"
      borderRadius="full"
      display="inline-block"
      lineHeight="1"
      textSize="md"
      {...rest}
    >
      <Icon icon={icon} />
    </Box>
  );
}
